import { useAuth, useOrganizationList } from '@clerk/react';
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../lib/api-client';

export function OrganizationSetupPage() {
  const { isLoaded, isSignedIn, orgId, orgRole } = useAuth();
  const organizations = useOrganizationList({ userMemberships: { infinite: true } });
  const navigate = useNavigate();
  const [accessMessage, setAccessMessage] = useState<string | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [organizationBusy, setOrganizationBusy] = useState(false);
  const [membershipTimeout, setMembershipTimeout] = useState(false);

  const memberships = organizations.isLoaded ? organizations.userMemberships.data : [];
  const activeMembership = memberships.find((membership) => membership.organization.id === orgId);

  const activateOrganization = async (organizationId: string) => {
    if (!organizations.isLoaded) return;
    setOrganizationBusy(true);
    setOrganizationError(null);
    try {
      await organizations.setActive({ organization: organizationId });
      // Clerk has updated its active organization; then refresh the app session
      // and let the protected route perform the API authorization check.
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setOrganizationError(error instanceof Error ? error.message : 'Could not activate this organization.');
      setOrganizationBusy(false);
    }
  };

  const createOrganization = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!organizations.isLoaded || !organizationName.trim()) return;
    setOrganizationBusy(true);
    setOrganizationError(null);
    try {
      const organization = await organizations.createOrganization({ name: organizationName.trim() });
      await activateOrganization(organization.id);
    } catch (error) {
      setOrganizationError(error instanceof Error ? error.message : 'Could not create this organization.');
      setOrganizationBusy(false);
    }
  };

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !orgId) {
      setCheckingAccess(false);
      return;
    }
    let active = true;
    setCheckingAccess(true);
    setAccessMessage(null);
    api.get('/auth/me')
      .then(() => {
        if (active) navigate('/dashboard', { replace: true });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        setAccessMessage(message ?? 'LivinHome could not verify access to this organization. Please try again.');
      })
      .finally(() => {
        if (active) setCheckingAccess(false);
      });
    return () => { active = false; };
  }, [isLoaded, isSignedIn, orgId, orgRole, navigate]);

  useEffect(() => {
    const isWaiting = !isLoaded || !organizations.isLoaded || organizations.userMemberships.isLoading;
    if (!isWaiting) {
      setMembershipTimeout(false);
      return;
    }
    const timer = window.setTimeout(() => setMembershipTimeout(true), 10_000);
    return () => window.clearTimeout(timer);
  }, [isLoaded, organizations.isLoaded, organizations.userMemberships.isLoading]);

  if (!isLoaded) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack spacing={2} alignItems="center" role="status">
          <CircularProgress />
          <Typography>Connecting to Clerk…</Typography>
          {membershipTimeout ? <Alert severity="error">
            Clerk did not finish loading. Refresh this page; if it repeats, check the Clerk domain and allowed origins.
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </Alert> : null}
        </Stack>
      </Box>
    );
  }
  if (!isSignedIn) return <Navigate to="/login" replace />;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography variant="h4" fontWeight={700}>Set up your LivinHome workspace</Typography>
          <Typography color="text.secondary" maxWidth={620}>
            Choose an organization you belong to, or create one. The first organization admin will be set up as the workspace owner.
            Other organization members need an active LivinHome staff account from their admin.
          </Typography>
          {accessMessage ? <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>{accessMessage}</Alert> : null}
          {!orgId ? <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>
            Select an organization to continue. If you just created one, select it below to activate it for this session.
          </Alert> : null}
          {checkingAccess ? <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} /><Typography variant="body2">Checking organization access…</Typography>
          </Stack> : null}
          {organizationError ? <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>{organizationError}</Alert> : null}
          <Box sx={{ pt: 2, width: '100%', maxWidth: 480 }}>
            {!organizations.isLoaded ? (
              membershipTimeout ? <Alert severity="error">
                Clerk is not ready to load organizations. Refresh this page and try again.
                <Button onClick={() => window.location.reload()}>Reload</Button>
              </Alert> : <Stack spacing={1} alignItems="center" role="status">
                  <CircularProgress size={24} />
                  <Typography variant="body2">Loading your Clerk organizations…</Typography>
                </Stack>
            ) : organizations.userMemberships.isError ? (
              <Alert severity="error">
                {organizations.userMemberships.error?.errors?.[0]?.longMessage ?? 'Clerk could not load your organization memberships. Refresh and try again.'}
              </Alert>
            ) : organizations.userMemberships.isLoading && !membershipTimeout ? (
              <Stack spacing={1} alignItems="center" role="status">
                <CircularProgress size={24} />
                <Typography variant="body2">Loading your Clerk organizations…</Typography>
              </Stack>
            ) : organizations.userMemberships.isLoading ? (
              <Alert severity="error">Clerk did not return your organizations. Check the Clerk dashboard configuration and reload this page.</Alert>
            ) : (
              <Stack spacing={2}>
                {activeMembership ? (
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={organizationBusy}
                    onClick={() => void activateOrganization(activeMembership.organization.id)}
                  >
                    {organizationBusy ? 'Connecting…' : `Continue to ${activeMembership.organization.name}`}
                  </Button>
                ) : null}
                {memberships.filter((membership) => membership.organization.id !== orgId).map((membership) => (
                  <Button
                    key={membership.id}
                    fullWidth
                    variant="outlined"
                    disabled={organizationBusy}
                    onClick={() => void activateOrganization(membership.organization.id)}
                  >
                    Use {membership.organization.name}
                  </Button>
                ))}
                <Box component="form" onSubmit={(event) => void createOrganization(event)}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {memberships.length ? 'Create another organization' : 'Create your organization'}
                    </Typography>
                    <TextField
                      fullWidth
                      label="Organization name"
                      value={organizationName}
                      onChange={(event) => setOrganizationName(event.target.value)}
                      required
                      inputProps={{ maxLength: 100 }}
                    />
                    <Button type="submit" variant="contained" disabled={organizationBusy || !organizationName.trim()}>
                      {organizationBusy ? 'Creating…' : 'Create organization'}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}
